import { COUNTRY_FEATURES } from "@/lib/globe-features";

/* Shared dot-sphere geometry for the particle Earths (the Market Research card and
   the Global Network section). Country polygons from the world atlas are rasterised
   once into an equirectangular land mask; fibonacci samples are then tested against
   it, so the dome reads as real continents instead of random noise. A sparse ocean
   layer keeps the sphere silhouette readable.

   Browser-only: the mask needs a canvas. Callers guard with a `typeof document`
   check and fall back to a uniform sphere. */

export type GlobeDotKind = 0 | 1 | 2; // 0 ink · 1 green · 2 yellow

export interface GlobeDot {
  ux: number;
  uy: number;
  uz: number;
  size: number;
  alpha: number;
  kind: GlobeDotKind;
}

export interface GlobeDotOptions {
  /** Fibonacci samples tested against the land mask. */
  landSamples?: number;
  /** Sparse ocean samples that keep the sphere silhouette. */
  oceanSamples?: number;
  /** Latitudes below this are dropped — used by the cropped card dome, which only
   *  shows the northern hemisphere. Pass -90 for a full sphere. */
  minLatDeg?: number;
  /** Fixed seed so the dot pattern is stable across renders and theme flips. */
  seed?: number;
}

export interface GlobeDotGeometry {
  land: GlobeDot[];
  ocean: GlobeDot[];
}

/** Deterministic PRNG so the dot pattern never reshuffles between renders. */
export function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface LandMask {
  data: Uint8ClampedArray;
  w: number;
  h: number;
}

let maskCache: LandMask | null = null;

/** Rasterise every country's outer rings into an equirectangular land mask. */
function buildLandMask(): LandMask | null {
  if (maskCache) return maskCache;
  const w = 720;
  const h = 360;
  const cnv = document.createElement("canvas");
  cnv.width = w;
  cnv.height = h;
  const ctx = cnv.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;

  ctx.fillStyle = "#fff";
  for (const country of COUNTRY_FEATURES) {
    if (country.name === "Antarctica") continue; // cropped out of frame anyway
    for (const ring of country.rings) {
      for (const shift of ring.crosses ? [0, -360] : [0]) {
        ctx.beginPath();
        for (let i = 0; i < ring.test.length; i++) {
          const x = ((ring.test[i][0] + shift + 180) / 360) * w;
          const y = ((90 - ring.test[i][1]) / 180) * h;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
      }
    }
  }
  maskCache = { data: ctx.getImageData(0, 0, w, h).data, w, h };
  return maskCache;
}

const geomCache = new Map<string, GlobeDotGeometry>();

/** Land and ocean dot fields for a unit sphere. Results are cached per option set,
 *  so several globes on one page share the rasterisation work. */
export function getGlobeDots(options: GlobeDotOptions = {}): GlobeDotGeometry {
  const landSamples = options.landSamples ?? 9000;
  const oceanSamples = options.oceanSamples ?? 1400;
  const minLatDeg = options.minLatDeg ?? -58;
  const seed = options.seed ?? 20260722;

  const key = `${landSamples}|${oceanSamples}|${minLatDeg}|${seed}`;
  const cached = geomCache.get(key);
  if (cached) return cached;

  const mask = buildLandMask();
  const rand = mulberry32(seed);
  const land: GlobeDot[] = [];
  const ocean: GlobeDot[] = [];

  const isLand = (lonDeg: number, latDeg: number): boolean => {
    if (!mask) return true; // fallback: uniform sphere
    const x = Math.min(mask.w - 1, Math.max(0, Math.floor(((lonDeg + 180) / 360) * mask.w)));
    const y = Math.min(mask.h - 1, Math.max(0, Math.floor(((90 - latDeg) / 180) * mask.h)));
    return mask.data[(y * mask.w + x) * 4 + 3] > 0;
  };

  const ga = Math.PI * (3 - Math.sqrt(5));
  const sample = (
    count: number,
    offset: number,
    wantLand: boolean,
    out: GlobeDot[],
    faint: boolean
  ) => {
    for (let i = 0; i < count; i++) {
      const uy = 1 - (i / (count - 1)) * 2;
      const latDeg = (Math.asin(Math.max(-1, Math.min(1, uy))) * 180) / Math.PI;
      if (latDeg < minLatDeg) continue;
      const th = ga * (i + offset);
      const rr = Math.sqrt(Math.max(0, 1 - uy * uy));
      const ux = Math.cos(th) * rr;
      const uz = Math.sin(th) * rr;
      const lonDeg = (Math.atan2(uz, ux) * 180) / Math.PI;
      if (isLand(lonDeg, latDeg) !== wantLand) continue;

      // small jitter breaks the fibonacci lattice so the field looks organic
      const j = 0.012;
      const jx = ux + (rand() - 0.5) * j;
      const jy = uy + (rand() - 0.5) * j;
      const jz = uz + (rand() - 0.5) * j;
      const len = Math.hypot(jx, jy, jz) || 1;

      out.push({
        ux: jx / len,
        uy: jy / len,
        uz: jz / len,
        // every static dot is the same size and gray; colour + rings live on the markers
        size: faint ? 0.7 : 1.0,
        alpha: faint ? 0.05 + rand() * 0.05 : 0.3 + rand() * 0.5,
        kind: 0,
      });
    }
  };

  sample(landSamples, 0, true, land, false);
  sample(oceanSamples, 0.5, false, ocean, true);

  const geom = { land, ocean };
  geomCache.set(key, geom);
  return geom;
}
